import type { EnvironmentStatus } from "@/lib/api";
import { ResourceStatus } from "@/components/common/ResourceCard";
import type { StatusDotTone } from "@/components/common/StatusDot";

/** 環境コンテナ状態の取得フェーズ。loading = 取得中、loaded = 取得済み、error = 取得失敗。 */
export type EnvironmentStatusLoadState = "loading" | "loaded" | "error";

const STATUS_THEME: Record<
	EnvironmentStatus,
	{ label: string; tone: StatusDotTone; indicator: "dot" | "ping" | "pulse"; title?: string }
> = {
	running: { label: "稼働中", tone: "success", indicator: "ping" },
	pending: { label: "起動準備中", tone: "warning", indicator: "pulse" },
	waitingForCapacity: {
		label: "インスタンス確保待ち",
		tone: "warning",
		indicator: "pulse",
		// ラベルだけでは「待てば起動する」「取り消せる」が伝わらないため補足する。
		title: "選択したインスタンスに空きが出るまで自動で再試行しています。停止で取り消せます。",
	},
	shuttingDown: { label: "終了処理中", tone: "warning", indicator: "pulse" },
	errored: { label: "異常", tone: "danger", indicator: "dot" },
	startupFailed: { label: "起動失敗", tone: "danger", indicator: "dot" },
	notRunning: { label: "休止中", tone: "muted", indicator: "dot" },
};

/**
 * 環境コンテナの起動状態を色付きの丸と文言で表示する。取得中はスピナー、取得失敗時は「不明」を出す。
 * 異常 (errored) の詳細は、隣の情報ボタンから開くダイアログ (EnvironmentErrorDialog) で示す。
 */
export function EnvironmentStatusBadge({
	status,
	state,
}: {
	status: EnvironmentStatus | undefined;
	state: EnvironmentStatusLoadState;
}) {
	if (state === "loading") return <ResourceStatus tone="muted" indicator="spinner" />;

	if (state === "error" || status === undefined) {
		return (
			<ResourceStatus tone="muted" tooltip="状態を取得できませんでした">
				不明
			</ResourceStatus>
		);
	}

	const theme = STATUS_THEME[status];
	return (
		<ResourceStatus tone={theme.tone} indicator={theme.indicator} tooltip={theme.title}>
			{theme.label}
		</ResourceStatus>
	);
}
